import React from 'react'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Collapse from '@mui/material/Collapse'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined'
import CodeIcon from '@mui/icons-material/CodeOutlined'

const PRESETS = [
  {
    id: 'none',
    labelKey: 'common.none',
    params: {},
  },
  {
    id: 'sourcemappings',
    labelKey: 'reference.source_mappings',
    params: { method: 'sourcemappings' },
  },
  {
    id: 'sourcetoconcepts',
    labelKey: 'reference.all_source_concepts_and_mappings',
    params: { method: 'sourcetoconcepts', mapTypes: 'Q-AND-A,CONCEPT-SET', cascadeLevels: '*', returnMapTypes: '*' },
  },
  {
    id: 'custom',
    labelKey: 'common.custom',
    params: null,
  },
]

const DEFAULT_CUSTOM_PARAMS = {
  method: 'sourcetoconcepts',
  mapTypes: '',
  excludeMapTypes: '',
  cascadeLevels: '*',
  returnMapTypes: '*',
}

const buildQueryString = (params, transform) => {
  if (!params || Object.keys(params).length === 0) return ''
  const parts = []
  if (params.method) parts.push(`method=${params.method}`)
  if (params.mapTypes) parts.push(`mapTypes=${params.mapTypes}`)
  if (params.excludeMapTypes) parts.push(`excludeMapTypes=${params.excludeMapTypes}`)
  if (params.cascadeLevels) parts.push(`cascadeLevels=${params.cascadeLevels}`)
  if (params.returnMapTypes) parts.push(`returnMapTypes=${params.returnMapTypes}`)
  if (transform) parts.push('transformReferences=openmrs_concept_reference')
  return parts.length ? `?${parts.join('&')}` : ''
}

const CascadeSelector = ({ onChange, conceptUrl, collectionUrl, showPreviewDefault = false }) => {
  const { t } = useTranslation()
  const [selectedPresetId, setSelectedPresetId] = React.useState('none')
  const [transform, setTransform] = React.useState(false)
  const [customParams, setCustomParams] = React.useState(DEFAULT_CUSTOM_PARAMS)
  const [showPreview, setShowPreview] = React.useState(showPreviewDefault)
  const [showAdvanced, setShowAdvanced] = React.useState(false)

  const selectedPreset = PRESETS.find(p => p.id === selectedPresetId)
  const baseParams = selectedPresetId === 'custom' ? customParams : (selectedPreset.params || {})
  const currentParams = transform
    ? { ...baseParams, transformReferences: 'openmrs_concept_reference' }
    : baseParams

  React.useEffect(() => {
    if (onChange) onChange(currentParams)
  }, [onChange, selectedPresetId, customParams, transform])

  const handleCustomParamChange = field => event => {
    setCustomParams(prev => ({ ...prev, [field]: event.target.value }))
  }

  const queryString = buildQueryString(baseParams, transform)
  const collectionBase = (collectionUrl || '/:owner/collections/:collection').replace(/\/$/, '')
  const endpoint = `${collectionBase}/references/${queryString}`
  const conceptExpr = conceptUrl || '/:owner/sources/:source/concepts/:id/'
  const requestBody = JSON.stringify({ data: { expressions: [conceptExpr] }, cascade: baseParams.method || '' }, null, 2)

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>{t('reference.cascade')}</InputLabel>
          <Select
            value={selectedPresetId}
            label={t('reference.cascade')}
            onChange={e => setSelectedPresetId(e.target.value)}
          >
            {PRESETS.map(preset => (
              <MenuItem key={preset.id} value={preset.id}>{t(preset.labelKey)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Custom params form */}
      <Collapse in={selectedPresetId === 'custom'} unmountOnExit>
        <Box
          sx={{
            mt: 1.5,
            pl: 1.5,
            borderLeft: '2px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <FormControl size="small" fullWidth>
            <InputLabel>{t('reference.method_label')}</InputLabel>
            <Select
              value={customParams.method}
              label={t('reference.method_label')}
              onChange={handleCustomParamChange('method')}
            >
              <MenuItem value="sourcetoconcepts">sourcetoconcepts — {t('reference.method_to_concepts')}</MenuItem>
              <MenuItem value="sourcemappings">sourcemappings — {t('reference.method_mappings_only')}</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            label={t('mapping.map_types')}
            placeholder={t('cloneToSource.map_types_placeholder')}
            value={customParams.mapTypes}
            onChange={handleCustomParamChange('mapTypes')}
            helperText={t('reference.map_types_help')}
            fullWidth
          />

          <TextField
            size="small"
            label={t('cloneToSource.exclude_map_types')}
            placeholder={t('reference.exclude_map_types_placeholder')}
            value={customParams.excludeMapTypes}
            onChange={handleCustomParamChange('excludeMapTypes')}
            helperText={t('reference.exclude_map_types_help')}
            fullWidth
          />

          <TextField
            size="small"
            label={t('cloneToSource.cascade_levels')}
            placeholder={t('reference.cascade_levels_placeholder')}
            value={customParams.cascadeLevels}
            onChange={handleCustomParamChange('cascadeLevels')}
            helperText={t('reference.cascade_levels_help')}
            fullWidth
          />

          <TextField
            size="small"
            label={t('cloneToSource.return_map_types')}
            placeholder={t('reference.return_map_types_placeholder')}
            value={customParams.returnMapTypes}
            onChange={handleCustomParamChange('returnMapTypes')}
            helperText={t('reference.return_map_types_help')}
            fullWidth
          />
        </Box>
      </Collapse>

      {/* Advanced options */}
      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          size="small"
          label={showAdvanced ? t('reference.hide_advanced') : t('reference.advanced')}
          variant="outlined"
          clickable
          onClick={() => setShowAdvanced(v => !v)}
          sx={{ fontSize: '0.7rem' }}
        />
      </Box>

      <Collapse in={showAdvanced} unmountOnExit>
        <Box sx={{ mt: 1.5, pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={transform}
                  onChange={e => setTransform(e.target.checked)}
                />
              }
              label={t('reference.transform')}
              sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
            <Tooltip
              title={t('reference.transform_tooltip')}
              placement="top"
              arrow
            >
              <IconButton size="small" tabIndex={-1} sx={{ p: 0.25, color: 'text.secondary' }}>
                <HelpOutlineIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Collapse>

      {/* API call preview */}
      <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          size="small"
          icon={<CodeIcon />}
          label={showPreview ? t('cloneToSource.hide_api_call') : t('cloneToSource.preview_api_call')}
          variant="outlined"
          clickable
          onClick={() => setShowPreview(v => !v)}
          sx={{ fontSize: '0.7rem' }}
        />
      </Box>

      <Collapse in={showPreview} unmountOnExit>
        <Box
          sx={{
            mt: 1,
            p: 1.25,
            bgcolor: 'grey.100',
            borderRadius: 1,
            fontFamily: 'monospace', // eslint-disable-line spellcheck/spell-checker
            fontSize: '0.72rem',
            wordBreak: 'break-all',
            color: 'text.secondary',
            whiteSpace: 'pre-wrap',
          }}
        >
          {`PUT ${endpoint}\n\n${requestBody}`}
        </Box>
      </Collapse>
    </Box>
  )
}

export default CascadeSelector
